import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import type { Session } from '@supabase/supabase-js';
import { useAuthStore } from '@/stores/auth.store';
import { authApiV2 } from '@/api/endpoints/auth';
import { getApiErrorMessage } from '@/api/client';
import { AuthMarketingShell } from '@/features/auth/components/AuthMarketingShell';
import {
  ImpersonationHandoffError,
  runImpersonationHandoff,
} from '@/features/auth/lib/impersonation-handoff';
import { performSignOut } from '@/features/auth/lib/sign-out';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { normalizePostAuthPath } from '@/lib/default-landing';
import type { UserSession } from '@/types/auth';

/**
 * Lands at /auth/sso/callback after Supabase Azure OAuth (PKCE) or the
 * retired Express SSO fragment redirect:
 *
 *   /auth/sso/callback?returnTo=/dashboard#…   (Supabase)
 *   /auth/sso/callback#accessToken=...&refreshToken=...&returnTo=...
 *
 * or on error:
 *
 *   /auth/sso/callback#error=pending_approval&message=...
 */
function safeReturnTo(raw: string | null | undefined): string {
  return normalizePostAuthPath(raw ?? undefined);
}

/** Map `/auth/sso/link` + `/auth/me` failures onto user-facing SSO copy. */
function ssoErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const status = error.response?.status;
    // Vite returns 502 when the /api proxy target (TS server :4000) is down.
    if (
      status === 502 ||
      status === 503 ||
      status === 504 ||
      error.code === 'ERR_NETWORK' ||
      error.code === 'ECONNREFUSED'
    ) {
      return import.meta.env.DEV
        ? 'TypeScript backend is not reachable (proxy → :4000). Start it with: cd server && npm run dev'
        : 'Unable to reach the server. Please try again.';
    }
    const data = error.response?.data as
      | { error?: { code?: string; message?: string } }
      | undefined;
    const code = data?.error?.code;
    const message = data?.error?.message;
    if (code === 'pending_approval') {
      return 'Your account is pending administrator approval.';
    }
    if (code === 'account_disabled') {
      return 'Account is disabled. Please contact your administrator.';
    }
    if (code === 'wrong_auth_provider') {
      return 'This account must sign in with email and password.';
    }
    if (code === 'sso_not_entra') {
      return 'Microsoft SSO is only available for Entra-linked accounts.';
    }
    if (status === 401) {
      return import.meta.env.DEV
        ? 'Session could not be verified. Ensure server SUPABASE_URL matches truspace-web VITE_SUPABASE_URL.'
        : 'Unable to load your account. Please try again.';
    }
    if (message) return message;
  }
  return getApiErrorMessage(error, 'Unable to load your account. Please try again.');
}

/**
 * Dedupes React StrictMode double-mount (getSession + onAuthStateChange) so two
 * parallel JIT `/sso/link` calls cannot race-create the same internal SSI row.
 */
let hydrateSsoInFlight: Promise<UserSession> | null = null;

/**
 * After Supabase Azure OAuth, ensure the legacy user row is linked to the OAuth
 * auth UUID, refresh the session so the CAT hook injects app_user_id, then hydrate.
 */
async function hydrateSsoSession(
  session: Session,
  setTokens: (tokens: { accessToken: string; refreshToken: string }) => void,
): Promise<UserSession> {
  if (hydrateSsoInFlight) {
    return hydrateSsoInFlight;
  }

  hydrateSsoInFlight = (async () => {
    setTokens({
      accessToken: session.access_token,
      refreshToken: session.refresh_token ?? '',
    });

    // When GoTrue linked Azure to the migrated shell, the CAT hook may already
    // inject app_user_id — skip the link step and hydrate directly.
    try {
      const me = await authApiV2.me();
      return me;
    } catch (err) {
      if (!isAxiosError(err) || err.response?.status !== 401) {
        throw err;
      }
    }

    await authApiV2.linkSsoSession();

    const supabase = getSupabaseClient();
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      throw error;
    }
    const refreshed = data.session ?? session;
    setTokens({
      accessToken: refreshed.access_token,
      refreshToken: refreshed.refresh_token ?? '',
    });

    return authApiV2.me();
  })().finally(() => {
    hydrateSsoInFlight = null;
  });

  return hydrateSsoInFlight;
}

export function SsoCallbackPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { setTokens, setUser, setCompanyFlags, setBuildings } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Supabase Microsoft/Entra OAuth return (PKCE): supabase-js consumes the
    // `?code=` and establishes the session. We wait for it, bridge the token,
    // hydrate the rich session, then route. The legacy Express-SSO path (tokens
    // in the URL hash) is handled below.
    if (isSupabaseLoginEnabled() && !window.location.hash.includes('accessToken')) {
      const supabase = getSupabaseClient();
      const returnTo = safeReturnTo(searchParams.get('returnTo'));
      let settled = false;
      const qs = new URLSearchParams(window.location.search);
      // Surface GoTrue/OAuth errors from the query/hash immediately (don't wait for timeout).
      // GoTrue often puts the failure in the hash after a failed Azure code exchange.
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const rawOauthError =
        qs.get('error_description') ||
        hashParams.get('error_description') ||
        qs.get('error') ||
        hashParams.get('error');
      if (rawOauthError) {
        const decoded = decodeURIComponent(rawOauthError.replace(/\+/g, ' '));
        // GoTrue appends the opaque auth code after this prefix — replace with an actionable message.
        if (decoded.toLowerCase().includes('unable to exchange external code')) {
          setError(
            'Microsoft SSO could not complete (invalid Azure client secret). ' +
              'In repo-root .env set SUPABASE_AUTH_EXTERNAL_AZURE_SECRET to the Secret Value ' +
              '(not Secret ID) from Truspace-V3-Auth → Certificates & secrets, then run ' +
              '`supabase stop && supabase start` and try again.',
          );
          return;
        }
        setError(decoded);
        return;
      }
      const finish = (session: Session) => {
        if (settled) return;
        settled = true;
        void hydrateSsoSession(session, setTokens)
          .then(async (s) => {
            setUser(s);
            if (s.companyFlags) setCompanyFlags(s.companyFlags);
            if (s.buildings) setBuildings(s.buildings);
            try {
              await runImpersonationHandoff(s, { blockOnFailure: true });
              queryClient.removeQueries({ queryKey: ['teams', 'partners'] });
            } catch (err) {
              // AC-IMP-006/007: parked target failed — do not fall through as SSI.
              await performSignOut();
              setError(
                err instanceof ImpersonationHandoffError
                  ? err.message
                  : getApiErrorMessage(
                      err,
                      'That account cannot be impersonated. Confirm the email and try again.',
                    ),
              );
              return;
            }
            window.history.replaceState({}, document.title, window.location.pathname);
            navigate(returnTo, { replace: true });
          })
          .catch((err) => {
            setError(ssoErrorMessage(err));
          });
      };
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session) finish(data.session);
      });
      const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
        if (session) finish(session);
      });
      const timer = setTimeout(() => {
        if (!settled) {
          setError('Single sign-on timed out. Please try again.');
        }
      }, 15_000);
      return () => {
        sub.subscription.unsubscribe();
        clearTimeout(timer);
      };
    }

    const hash = window.location.hash.replace(/^#/, '');
    const params = new URLSearchParams(hash);

    const err = params.get('error');
    if (err) {
      setError(params.get('message') || err);
      return;
    }

    const accessToken = params.get('accessToken');
    const refreshToken = params.get('refreshToken');
    const returnTo = normalizePostAuthPath(params.get('returnTo') ?? undefined);

    if (!accessToken || !refreshToken) {
      setError('Missing tokens from SSO callback.');
      return;
    }

    setTokens({ accessToken, refreshToken });

    void hydrateSsoSession(
      {
        access_token: accessToken,
        refresh_token: refreshToken,
      } as Session,
      setTokens,
    )
      .then((session) => {
        setUser(session);
        if (session.companyFlags) setCompanyFlags(session.companyFlags);
        if (session.buildings) setBuildings(session.buildings);
        window.history.replaceState({}, document.title, window.location.pathname);
        navigate(returnTo, { replace: true });
      })
      .catch((err) => {
        setError(ssoErrorMessage(err));
      });
  }, [navigate, queryClient, searchParams, setTokens, setUser, setCompanyFlags, setBuildings]);

  if (error) {
    return (
      <AuthMarketingShell
        title="Single sign-on failed"
        subtitle={error}
      >
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Back to sign-in
        </button>
      </AuthMarketingShell>
    );
  }

  return (
    <AuthMarketingShell
      title="Signing you in"
      subtitle="Finishing Microsoft sign-in…"
      isLoading
    />
  );
}
